import { reactive } from 'vue';
import _ from 'lodash';
import { fetchWrapper } from '@/helpers';

const localApiUrl = process.env.VUE_APP_PROXY_SERVER + '/api';

const layoutList = reactive([]);
const fieldList = reactive({});
const statusList = reactive({});
const priorityList = reactive({});
const workflowList = reactive({});
const screenList = reactive({});
const optionMatrixList = reactive({});
const customData = reactive([]);

class TicketApi {
  // Load Data
  async fetchLayout(refresh = false) {
    if (refresh || Object.keys(layoutList).length === 0) {
      layoutList.value = await fetchWrapper.get(
        localApiUrl + '/Layout/AllLayout'
      );
    } else {
      var updated = false;
      await Promise.all(
        layoutList.value.layouts.map(async (item, index) => {
          var request = {
            id: item.id,
            revision: item.revision,
          };
          var result = await fetchWrapper.post(
            localApiUrl + '/Layout/CheckRevision',
            request
          );

          if (!result.isUpdate) {
            layoutList.value.layouts[index] = result.layout;
            updated = true;

            await updateMasterData('layout');
          }
        })
      );
    }

    return _.cloneDeep(layoutList.value.layouts);
  }

  async getLayoutById(id) {
    return await fetchWrapper.get(`${localApiUrl}/Layout/Get?id=${id}`);
  }

  async fetchField(refresh = false) {
    if (refresh || Object.keys(fieldList).length === 0)
      fieldList.value = await fetchWrapper.get(localApiUrl + '/Field/AllField');
    else {
      var result = await fetchWrapper.get(localApiUrl + '/Field/LastUpdate');

      if (result != fieldList.value.lastUpdate) {
        await updateMasterData('field');
      }
    }

    return JSON.parse(JSON.stringify(fieldList.value));
  }

  async fetchStatus(refresh = false) {
    if (refresh || Object.keys(statusList).length === 0)
      statusList.value = await fetchWrapper.get(localApiUrl + '/Status/List');
    else {
      var maxDateObject = statusList.value.reduce((max, item) =>
        new Date(item.updatedDateTime) > new Date(max.updatedDateTime)
          ? item
          : max
      );

      var result = await fetchWrapper.get(localApiUrl + '/Status/LastUpdate');

      if (result != maxDateObject.updatedDateTime) {
        await updateMasterData('status');
      }
    }

    return JSON.parse(JSON.stringify(statusList.value));
  }

  async fetchPriority(refresh = false) {
    if (refresh || Object.keys(priorityList).length === 0)
      priorityList.value = await fetchWrapper.get(
        localApiUrl + '/Priority/List'
      );
    else {
      var maxDateObject = priorityList.value.reduce((max, item) =>
        new Date(item.updatedDateTime) > new Date(max.updatedDateTime)
          ? item
          : max
      );

      var result = await fetchWrapper.get(localApiUrl + '/Priority/LastUpdate');

      if (result != maxDateObject.updatedDateTime) {
        await updateMasterData('priority');
      }
    }

    return JSON.parse(JSON.stringify(priorityList.value));
  }

  async fetchWorkflow(refresh = false) {
    if (refresh || Object.keys(workflowList).length === 0)
      workflowList.value = await fetchWrapper.get(
        localApiUrl + '/Workflow/AllWorkFlow'
      );
    else {
      var result = await fetchWrapper.get(localApiUrl + '/Workflow/LastUpdate');

      if (result != workflowList.value.lastUpdate) {
        await updateMasterData('workflow');
      }
    }

    return JSON.parse(JSON.stringify(workflowList.value));
  }

  async fetchScreen(refresh = false) {
    if (refresh || Object.keys(screenList).length === 0)
      screenList.value = await fetchWrapper.get(localApiUrl + '/Screen/List');
    else {
      var result = await fetchWrapper.get(localApiUrl + '/Screen/LastUpdate');

      if (result != screenList.value.lastUpdate) {
        await updateMasterData('screen');
      }
    }

    return JSON.parse(JSON.stringify(screenList.value));
  }

  async fetchOptionMatrix(refresh = false) {
    if (refresh || Object.keys(optionMatrixList).length === 0)
      optionMatrixList.value = await fetchWrapper.get(
        localApiUrl + '/OptionMatrix/List'
      );

    return JSON.parse(JSON.stringify(optionMatrixList.value));
  }

  async getAllLayout() {
    return await fetchWrapper.get(localApiUrl + '/Layout/AllLayout');
  }

  async getAllField() {
    return await fetchWrapper.get(localApiUrl + '/Field/AllField');
  }

  async getAllStatus() {
    return await fetchWrapper.get(localApiUrl + '/Status/List');
  }

  async getAllWorkFlow() {
    return await fetchWrapper.get(localApiUrl + '/WorkFlow/AllWorkFlow');
  }

  async getAllScreen() {
    return await fetchWrapper.get(localApiUrl + '/Screen/List');
  }

  async getAllOptionMatrix() {
    return await fetchWrapper.get(localApiUrl + '/OptionMatrix/List');
  }

  async getAllPriority() {
    return await fetchWrapper.get(localApiUrl + '/Priority/List');
  }

  async checkRevisionLayout(request) {
    return await fetchWrapper.post(
      localApiUrl + '/Layout/CheckRevision',
      request
    );
  }

  async checkUpdateField() {
    return await fetchWrapper.get(localApiUrl + '/Field/LastUpdate');
  }

  async checkUpdateStatus() {
    return await fetchWrapper.get(localApiUrl + '/Status/LastUpdate');
  }

  async checkUpdateWorkflow() {
    return await fetchWrapper.get(localApiUrl + '/Workflow/LastUpdate');
  }

  async checkUpdateScreen() {
    return await fetchWrapper.get(localApiUrl + '/Screen/LastUpdate');
  }

  async checkUpdatePriority() {
    return await fetchWrapper.get(localApiUrl + '/Priority/LastUpdate');
  }

  async fetchCustomData(id) {
    if (Object.keys(customData).length === 0 && customData[id] === undefined)
      customData[id] = await fetchWrapper.get(
        localApiUrl + '/Custom/Get?id=' + id
      );

    return JSON.parse(JSON.stringify(customData[id]));
  }

  async searchMagicAsset(request) {
    return await fetchWrapper.post(
      `${localApiUrl}/Custom/SearchMagicAsset`,
      request
    );
  }

  async getMagicAssetById(request) {
    return await fetchWrapper.post(
      `${localApiUrl}/Custom/GetMagicAssetById`,
      request
    );
  }

  async getMagicAssetView(fieldId, name) {
    return await fetchWrapper.get(
      `${localApiUrl}/Custom/GetMagicAssetView?fieldId=${fieldId}&name=${name}`
    );
  }

  async getMagicAssetField(fieldId) {
    return await fetchWrapper.get(
      `${localApiUrl}/Custom/GetMagicAssetField?fieldId=${fieldId}`
    );
  }

  async getMagicAssetDataType(fieldId) {
    return await fetchWrapper.get(
      `${localApiUrl}/Custom/GetMagicAssetDataType?fieldId=${fieldId}`
    );
  }

  async fetchUserCustomData() {
    return fetchWrapper.get(localApiUrl + '/User/CustomList');
  }

  async searchTicketV3(searchRequest) {
    return fetchWrapper.post(localApiUrl + '/Ticket/SearchV3', searchRequest);
  }

  async searchGroup(searchRequest) {
    return fetchWrapper.post(
      localApiUrl + '/Ticket/SearchGroup',
      searchRequest
    );
  }

  async getTicket(id) {
    let url = localApiUrl.concat('/Ticket/Get?ticket_id=', id);

    return fetchWrapper.get(url);
  }

  async createTicket(ticket) {
    return fetchWrapper.post(localApiUrl + '/Ticket/Create', ticket);
  }

  async updateTicket(ticket) {
    return fetchWrapper.patch(localApiUrl + '/Ticket/Update', ticket);
  }

  async patchFieldValue(request) {
    try {
      const url = localApiUrl + '/Ticket/PatchFieldValue';

      return await fetchWrapper.patch(url, request);
    } catch (err) {
      return err;
    }
  }

  async deleteFieldValue(request) {
    const url = localApiUrl + '/Ticket/DeleteFieldValue';
    return await fetchWrapper.delete(url, request);
  }

  async getTicketCustomData(fieldType, ids) {
    var request = {
      fieldType: fieldType,
      ids: ids,
    };

    return fetchWrapper.post(localApiUrl + '/Custom/Get', request);
  }

  async searchCustom(fieldType, searchText, groupIds = []) {
    var request = {
      fieldType: fieldType,
      textSearch: searchText,
      filterGroups: groupIds,
    };

    return fetchWrapper.post(localApiUrl + '/Custom/Search', request);
  }

  async deleteTicket(id, pageName) {
    let url = localApiUrl.concat(
      '/Ticket/Delete?ticket_id=',
      id,
      '&pageName=',
      pageName
    );

    return fetchWrapper.delete(url);
  }

  async getStatusScreen(layout, statusId) {
    var workflowId = layout.value.workflow.id;
    let url =
      localApiUrl +
      '/Workflow/GetStatusScreen?workflowId=' +
      workflowId +
      '&statusId=' +
      statusId;
    let screen = await fetchWrapper.get(url);
    setScreenField(layout, screen);

    return true;
  }

  async transitTicket(ticket) {
    return fetchWrapper.patch(localApiUrl + '/Ticket/Transition', ticket);
  }

  async getTicketLogDetail(ids) {
    return fetchWrapper.post(localApiUrl + '/Ticket/GetTicketLogDetail', ids);
  }

  async exportExcel(data) {
    var url = localApiUrl + '/Ticket/ExportExcel';

    return fetchWrapper.postBlob(url, data);
  }

  async exportCsv(data) {
    var url = localApiUrl + '/Ticket/ExportCsv';

    return fetchWrapper.postBlob(url, data);
  }

  async exportExcelPivot(data) {
    var url = localApiUrl + '/Ticket/ExportExcelPivot';

    return fetchWrapper.postBlob(url, data);
  }

  async exportCsvPivot(data) {
    var url = localApiUrl + '/Ticket/ExportCsvPivot';

    return fetchWrapper.postBlob(url, data);
  }

  async downloadTemplateFile(layoutId) {
    var url = localApiUrl + '/Ticket/GetImportTemplate?layoutId=' + layoutId;

    return fetchWrapper.getBlob(url);
  }

  async importTicket(data) {
    var url = localApiUrl + '/Ticket/ImportTicket';

    return fetchWrapper.uploadFile(url, data);
  }

  async confirmImport(data) {
    var url = localApiUrl + '/Ticket/ConfirmImport';

    return fetchWrapper.post(url, data);
  }
}

async function updateMasterData(type) {
  switch (type) {
    case 'field':
      fieldList.value = await fetchWrapper.get(localApiUrl + '/Field/AllField');
      layoutList.value = await fetchWrapper.get(
        localApiUrl + '/Layout/AllLayout'
      );
      screenList.value = await fetchWrapper.get(localApiUrl + '/Screen/List');
      workflowList.value = await fetchWrapper.get(
        localApiUrl + '/Workflow/AllWorkFlow'
      );
      break;
    case 'layout':
      statusList.value = await fetchWrapper.get(localApiUrl + '/Status/List');
      optionMatrixList.value = await fetchWrapper.get(
        localApiUrl + '/OptionMatrix/List'
      );
      break;
    case 'screen':
      layoutList.value = await fetchWrapper.get(
        localApiUrl + '/Layout/AllLayout'
      );
      screenList.value = await fetchWrapper.get(localApiUrl + '/Screen/List');
      workflowList.value = await fetchWrapper.get(
        localApiUrl + '/Workflow/AllWorkFlow'
      );
      break;
    case 'priority':
    case 'status':
      layoutList.value = await fetchWrapper.get(
        localApiUrl + '/Layout/AllLayout'
      );
      break;
    case 'workflow':
      layoutList.value = await fetchWrapper.get(
        localApiUrl + '/Layout/AllLayout'
      );
      workflowList.value = await fetchWrapper.get(
        localApiUrl + '/Workflow/AllWorkFlow'
      );
      break;
    default:
      break;
  }
}

function setScreenField(layout, screen) {
  layout.value.screenName = screen.name;

  let fields = layout.value.fields;

  // if no screen found, show all field
  if (screen.screenFields.length == 0) {
    fields.forEach((field) => {
      field.isShow = true;
      field.isEditable = true;
    });

    return true;
  }

  layout.value.fields.forEach((field) => {
    field.isShow = false;
    field.isEditable = false;

    let screenField = screen.screenFields.find((x) => x.fieldId == field.id);
    if (screenField != undefined) {
      field.isShow = true;
      field.isEditable = screenField.isEditable;
      field.isRequired = screenField.isRequired;
      field.order = screenField.order;
    }
  });

  // re-order fields for screen setting
  layout.value.fields = layout.value.fields.sort((a, b) => a.order - b.order);

  return true;
}

export default TicketApi;
